/* eslint-disable react/destructuring-assignment */

'use client';

import { DataTable, NextImageWithSkeleton, StepperInput } from '@packages/chakra-v3';
import {
	FormatNumber,
	HStack,
	IconButton,
	Text,
} from '@chakra-ui/react';

import { FaTrash } from 'react-icons/fa6';
import { useTranslations } from 'next-intl';

type CartItem = {
	id:       string;
	quantity: number;
	product: {
		id:    string;
		image: string;
		name:  string;
		stock: number;
	};
	unitPrice:           number;
	discountPrice:       number | null;
	effectiveUnitPrice:  number;
	effectiveTotalPrice: number;
};

type HandleUpdateCartItem = {
	cartItemId: string;
	quantity:   number;
};

type DisplayCartItemTableProps = {
	isLoading:              boolean;
	cartItems:              CartItem[] | undefined;
	isEditable:             boolean;
	onClickUpdateCartItem?: ({ cartItemId, quantity }: HandleUpdateCartItem) => void;
	onClickRemoveCartItem?: (cartId: string) => void;
};

const CartItemTable = (props: DisplayCartItemTableProps) => {
	const t = useTranslations();
	const isEditable = 'isEditable' in props ? props.isEditable : false;
	const { isLoading, cartItems } = props;

	const header = [
		{
			key:    'product',
			column: t('CartPage.table.column.product'),
			style:  { width: '60%' },
		},
		{
			key:    'price',
			column: t('CartPage.table.column.price'),
			style:  {
				width:     '10%',
				textAlign: 'right',
			},
		},
		{
			key:    'quantity',
			column: t('CartPage.table.column.qty'),
			style:  {
				width:     '10%',
				textAlign: 'center',
			},
		},
		{
			key:    'total',
			column: t('CartPage.table.column.total'),
			style:  {
				width:     '10%',
				textAlign: 'right',
			},
		},
	];

	if (isEditable) {
		header.push({
			key:    'action',
			column: '',
			style:  {
				width:     '10%',
				textAlign: 'right',
			},
		});
	}

	return (
		<DataTable
			headers={header}
			isLoading={isLoading}
			rowCount={cartItems?.length}
		>
			{cartItems?.map(cartItem => (
				<DataTable.Row key={cartItem.id}>
					<DataTable.Cell>
						<HStack alignItems={'center'} gap={2}>
							<NextImageWithSkeleton
								alt={'Product thumbnail'}
								aspectRatio={'4/3'}
								objectFit={'contain'}
								src={cartItem.product.image}
								width={'80px'}
							/>
							<Text>{cartItem.product.name}</Text>
						</HStack>
					</DataTable.Cell>
					<DataTable.Cell textAlign={'right'}>
						<HStack alignItems={'flex-end'} gap={2} justifyContent={'flex-end'}>
							{cartItem.discountPrice && (
								<Text
									color={'gray.500'}
									textDecoration={'line-through'}
									textStyle={'p4'}
								>
									<FormatNumber
										currency={'THB'}
										currencyDisplay={'narrowSymbol'}
										style={'currency'}
										value={cartItem.unitPrice}
									/>
								</Text>
							)}
							<FormatNumber
								currency={'THB'}
								currencyDisplay={'narrowSymbol'}
								style={'currency'}
								value={cartItem.effectiveUnitPrice}
							/>
						</HStack>
					</DataTable.Cell>
					<DataTable.Cell textAlign={'center'}>
						{isEditable
							? (
								<StepperInput
									defaultValue={cartItem.quantity.toString()}
									max={cartItem.product.stock}
									min={1}
									w={'fit-content'}
									onValueChange={e => props?.onClickUpdateCartItem && props.onClickUpdateCartItem({
										cartItemId: cartItem.id,
										quantity:   e.valueAsNumber,
									})}
								/>
							)
							: cartItem.quantity}
					</DataTable.Cell>
					<DataTable.Cell textAlign={'right'}>
						<FormatNumber
							currency={'THB'}
							currencyDisplay={'narrowSymbol'}
							style={'currency'}
							value={cartItem.effectiveTotalPrice}
						/>
					</DataTable.Cell>
					{isEditable && (
						<DataTable.Cell textAlign={'right'}>
							<IconButton
								aria-label={'Remove from cart'}
								size={'sm'}
								onClick={() => props?.onClickRemoveCartItem && props.onClickRemoveCartItem(cartItem.id)}
							>
								<FaTrash size={16} />
							</IconButton>
						</DataTable.Cell>
					)}
				</DataTable.Row>
			))}
		</DataTable>
	);
};

export default CartItemTable;
